import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Bot,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CheckCircle2,
  Columns3,
  GitBranch,
  Info,
  Link2,
  Loader2,
  Plus,
  Play,
  Save,
  Sparkles,
  Table2,
  Trash2,
  WandSparkles,
  X,
} from 'lucide-react';
import {
  Badge,
  Button,
  Input,
  Label,
  Progress,
  Textarea,
} from '../ui';
import type { BadgeProps } from '../ui/badge';
import { OntologyGraphCanvas } from './OntologyGraphCanvas';
import {
  getOntologyCatalogLinkStatus,
  ontologyCatalogLinkStatusLabel,
  resolveOntologyEntityAsset,
  type OntologyCatalogLinkStatus,
} from './ontologyGraph';
import type {
  OntologyEntity,
  OntologyJoinPath,
  OntologyMetric,
  OntologySemanticMapping,
  StructuredAssetAllowlistRow,
  WorkspaceOntology,
} from './structuredAnalyticsConfig';
import { cn } from '../../shared/lib/cn';

type Selection =
  | { kind: 'entity'; id: string }
  | { kind: 'businessMeaning'; id: string }
  | { kind: 'metric'; id: string }
  | { kind: 'join'; id: string }
  | null;

type ValidationIssue = {
  id: string;
  severity: 'error' | 'warning';
  label: string;
  detail: string;
};

type OntologyEditorView = 'graph' | 'sheet';
type OntologySheetTab = 'entities' | 'businessMeanings' | 'metrics' | 'relationships' | 'catalogLinks';

export interface OntologyEditorProps {
  ontology: WorkspaceOntology;
  allowlist: StructuredAssetAllowlistRow[];
  onSave?: (ontology: WorkspaceOntology) => void | Promise<void>;
  onClose?: () => void;
  onReviewDraft?: () => void;
  hasReviewableRuntimeChanges?: boolean;
  reviewDisabled?: boolean;
  reviewDisabledReason?: string;
  readOnly?: boolean;
  readinessLabel?: string;
  runtimePackLabel?: string;
  selectedAssetCount?: number;
  backendBlockerCount?: number;
  aiDraftDisabled?: boolean;
  aiDraftDisabledReason?: string;
  isAiDrafting?: boolean;
  isAiDraftFailed?: boolean;
  aiDraftProgressMessage?: string;
  aiDraftProgressStage?: string;
  aiDraftProgressPercent?: number;
  isAiDraftStreamConnected?: boolean;
  onDraftWithAI?: () => void;
  suggestionReviewAction?: React.ReactNode;
  generationExperience?: React.ReactNode;
  layoutStorageKey?: string;
}

export function OntologyEditor({
  ontology: initialOntology,
  allowlist,
  onSave,
  onClose,
  onReviewDraft,
  hasReviewableRuntimeChanges = false,
  reviewDisabled = false,
  reviewDisabledReason,
  readOnly = false,
  readinessLabel,
  runtimePackLabel = 'No active pack',
  selectedAssetCount,
  backendBlockerCount,
  aiDraftDisabled = true,
  aiDraftDisabledReason = 'Coming soon',
  isAiDrafting = false,
  isAiDraftFailed = false,
  aiDraftProgressMessage,
  aiDraftProgressStage,
  aiDraftProgressPercent,
  isAiDraftStreamConnected = false,
  onDraftWithAI,
  suggestionReviewAction,
  generationExperience,
  layoutStorageKey,
}: OntologyEditorProps) {
  const [ontology, setOntology] = useState<WorkspaceOntology>(() => normalizeOntologyDraft(initialOntology));
  const [activeView, setActiveView] = useState<OntologyEditorView>('graph');
  const [selection, setSelection] = useState<Selection>(null);
  const [inspectorCollapsed, setInspectorCollapsed] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [hasUnsavedManualEdits, setHasUnsavedManualEdits] = useState(false);
  const [hasSavedDraftForReview, setHasSavedDraftForReview] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const hasUnsavedManualEditsRef = useRef(false);

  useEffect(() => {
    hasUnsavedManualEditsRef.current = hasUnsavedManualEdits;
  }, [hasUnsavedManualEdits]);

  useEffect(() => {
    setOntology((current) => {
      if (!hasUnsavedManualEditsRef.current) {
        return normalizeOntologyDraft(initialOntology);
      }
      return mergeIncomingOntology(current, initialOntology);
    });
    setSelection((current) => current ?? selectFirstAvailable(initialOntology));
  }, [initialOntology]);

  const entityById = useMemo(
    () => new Map(ontology.entities.map((entity) => [entity.id, entity])),
    [ontology.entities],
  );
  const selectedAssetIds = useMemo(
    () => new Set(allowlist.filter((row) => row.selected).map((row) => row.id)),
    [allowlist],
  );
  const validationIssues = useMemo(
    () => validateOntology(ontology, allowlist),
    [allowlist, ontology],
  );
  const hardIssueCount = validationIssues.filter((issue) => issue.severity === 'error').length;
  const displayedSelectedAssetCount = selectedAssetCount ?? selectedAssetIds.size;
  const aiDraftDisplayPercent =
    isAiDraftFailed
      ? 100
      : typeof aiDraftProgressPercent === 'number'
        ? Math.min(100, Math.max(0, aiDraftProgressPercent))
        : undefined;
  const activationIssueCount = Math.max(backendBlockerCount ?? 0, hardIssueCount);
  const validationStatusLabel =
    activationIssueCount > 0
      ? `${activationIssueCount} activation blocker${activationIssueCount === 1 ? '' : 's'}`
      : validationIssues.length > 0
        ? `${validationIssues.length} issue${validationIssues.length === 1 ? '' : 's'}`
        : 'Ready for review';

  const selectedEntity =
    selection?.kind === 'entity' ? ontology.entities.find((entity) => entity.id === selection.id) ?? null : null;
  const selectedBusinessMeaning =
    selection?.kind === 'businessMeaning' ? (ontology.semanticMappings ?? []).find((mapping) => mapping.id === selection.id) ?? null : null;
  const selectedMetric =
    selection?.kind === 'metric' ? ontology.metrics.find((metric) => metric.id === selection.id) ?? null : null;
  const selectedJoin =
    selection?.kind === 'join' ? ontology.joins.find((join) => join.id === selection.id) ?? null : null;

  const updateEntity = (id: string, patch: Partial<OntologyEntity>) => {
    setHasUnsavedManualEdits(true);
    setHasSavedDraftForReview(false);
    setOntology((prev) => ({
      ...prev,
      entities: prev.entities.map((entity) => (entity.id === id ? { ...entity, ...patch } : entity)),
    }));
  };

  const updateMetric = (id: string, patch: Partial<OntologyMetric>) => {
    setHasUnsavedManualEdits(true);
    setHasSavedDraftForReview(false);
    setOntology((prev) => ({
      ...prev,
      metrics: prev.metrics.map((metric) => (metric.id === id ? { ...metric, ...patch } : metric)),
    }));
  };

  const updateBusinessMeaning = (id: string, patch: Partial<OntologySemanticMapping>) => {
    setHasUnsavedManualEdits(true);
    setHasSavedDraftForReview(false);
    setOntology((prev) => ({
      ...prev,
      semanticMappings: (prev.semanticMappings ?? []).map((mapping) => (mapping.id === id ? { ...mapping, ...patch } : mapping)),
    }));
  };

  const updateJoin = (id: string, patch: Partial<OntologyJoinPath>) => {
    setHasUnsavedManualEdits(true);
    setHasSavedDraftForReview(false);
    setOntology((prev) => ({
      ...prev,
      joins: prev.joins.map((join) => (join.id === id ? { ...join, ...patch } : join)),
    }));
  };

  const addEntity = () => {
    setHasUnsavedManualEdits(true);
    setHasSavedDraftForReview(false);
    const asset = allowlist.find((row) => row.selected) ?? allowlist[0];
    const entity: OntologyEntity = {
      id: `entity-${Date.now()}`,
      name: 'New entity',
      physicalTable: asset ? `table:${asset.id}` : '',
      description: '',
      synonyms: [],
    };
    setOntology((prev) => ({ ...prev, entities: [...prev.entities, entity] }));
    setSelection({ kind: 'entity', id: entity.id });
  };

  const addMetric = () => {
    setHasUnsavedManualEdits(true);
    setHasSavedDraftForReview(false);
    const entity = ontology.entities[0];
    if (!entity) {
      addEntity();
      return;
    }
    const metric: OntologyMetric = {
      id: `metric-${Date.now()}`,
      name: 'New metric',
      entityId: entity.id,
      expression: '',
      description: '',
    };
    setOntology((prev) => ({ ...prev, metrics: [...prev.metrics, metric] }));
    setSelection({ kind: 'metric', id: metric.id });
  };

  const addBusinessMeaning = () => {
    setHasUnsavedManualEdits(true);
    setHasSavedDraftForReview(false);
    const entity = ontology.entities[0];
    if (!entity) {
      addEntity();
      return;
    }
    const mapping: OntologySemanticMapping = {
      id: `meaning-${Date.now()}`,
      conceptKey: `${slugifyIdentifier(entity.name)}.new_business_meaning`,
      label: 'New business meaning',
      entityId: entity.id,
      physicalTable: entity.physicalTable,
      columnId: '',
      description: '',
      aliases: [],
      valueKind: 'dimension',
    };
    setOntology((prev) => ({ ...prev, semanticMappings: [...(prev.semanticMappings ?? []), mapping] }));
    setActiveView('sheet');
    setSelection({ kind: 'businessMeaning', id: mapping.id });
  };

  const createJoinBetweenEntities = (leftEntityId: string, rightEntityId: string) => {
    const leftEntity = ontology.entities.find((entity) => entity.id === leftEntityId);
    const rightEntity = ontology.entities.find((entity) => entity.id === rightEntityId);
    if (!leftEntity || !rightEntity) {
      setSelection(leftEntity ? { kind: 'entity', id: leftEntity.id } : null);
      return;
    }
    const existingJoin = ontology.joins.find(
      (join) =>
        (join.leftEntityId === leftEntity.id && join.rightEntityId === rightEntity.id) ||
        (join.leftEntityId === rightEntity.id && join.rightEntityId === leftEntity.id),
    );
    if (existingJoin) {
      setSelection({ kind: 'join', id: existingJoin.id });
      return;
    }
    setHasUnsavedManualEdits(true);
    setHasSavedDraftForReview(false);
    const join: OntologyJoinPath = {
      id: `join-${Date.now()}`,
      name: `${leftEntity.name} relates to ${rightEntity.name}`,
      leftEntityId: leftEntity.id,
      rightEntityId: rightEntity.id,
      joinCondition: '',
      relationshipKind: 'semantic_only',
      predicate: 'relates to',
      optionality: 'unknown',
      description: `${leftEntity.name} relates to ${rightEntity.name}. Add catalog-backed join evidence before runtime activation.`,
      status: 'draft',
    };
    setOntology((prev) => ({ ...prev, joins: [...prev.joins, join] }));
    setSelection({ kind: 'join', id: join.id });
  };

  const addJoin = () => {
    const [leftEntity, rightEntity] = ontology.entities;
    if (!leftEntity || !rightEntity) {
      setSelection(leftEntity ? { kind: 'entity', id: leftEntity.id } : null);
      return;
    }
    createJoinBetweenEntities(leftEntity.id, rightEntity.id);
  };

  const deleteSelected = () => {
    if (!selection || readOnly) return;
    setHasUnsavedManualEdits(true);
    setHasSavedDraftForReview(false);
    setOntology((prev) => {
      if (selection.kind === 'entity') {
        return {
          ...prev,
          entities: prev.entities.filter((entity) => entity.id !== selection.id),
          semanticMappings: (prev.semanticMappings ?? []).filter((mapping) => mapping.entityId !== selection.id),
          metrics: prev.metrics.filter((metric) => metric.entityId !== selection.id),
          joins: prev.joins.filter(
            (join) => join.leftEntityId !== selection.id && join.rightEntityId !== selection.id,
          ),
        };
      }
      if (selection.kind === 'businessMeaning') {
        return { ...prev, semanticMappings: (prev.semanticMappings ?? []).filter((mapping) => mapping.id !== selection.id) };
      }
      if (selection.kind === 'metric') {
        return { ...prev, metrics: prev.metrics.filter((metric) => metric.id !== selection.id) };
      }
      return { ...prev, joins: prev.joins.filter((join) => join.id !== selection.id) };
    });
    setSelection(null);
  };

  const addSynonym = (entity: OntologyEntity, value: string) => {
    const synonym = value.trim();
    if (!synonym || entity.synonyms.includes(synonym)) return;
    updateEntity(entity.id, { synonyms: [...entity.synonyms, synonym] });
  };

  const addBusinessMeaningAlias = (mapping: OntologySemanticMapping, value: string) => {
    const alias = value.trim();
    if (!alias || mapping.aliases.includes(alias)) return;
    updateBusinessMeaning(mapping.id, { aliases: [...mapping.aliases, alias] });
  };

  const clearDraft = () => {
    if (readOnly) return;
    const itemCount =
      ontology.entities.length +
      (ontology.semanticMappings?.length ?? 0) +
      ontology.metrics.length +
      ontology.joins.length;
    if (itemCount === 0) return;
    const confirmed = window.confirm(
      'Clear all ontology draft entities, business meanings, metrics, and relationships? Save the draft after clearing to persist the reset.',
    );
    if (!confirmed) return;
    setHasUnsavedManualEdits(true);
    setHasSavedDraftForReview(false);
    setOntology({ entities: [], semanticMappings: [], metrics: [], joins: [] });
    setSelection(null);
  };

  const saveDraft = async () => {
    if (isSavingDraft) return;
    setIsSavingDraft(true);
    try {
      await onSave?.(ontology);
      setHasUnsavedManualEdits(false);
      setHasSavedDraftForReview(true);
    } catch {
      setHasSavedDraftForReview(false);
    } finally {
      setIsSavingDraft(false);
    }
  };
  const reviewIsNextStep = !readOnly && Boolean(onReviewDraft) && !reviewDisabled && !hasUnsavedManualEdits && (hasSavedDraftForReview || hasReviewableRuntimeChanges);
  const resolvedReviewDisabled = reviewDisabled || hasUnsavedManualEdits || (!hasSavedDraftForReview && !hasReviewableRuntimeChanges);
  const resolvedReviewTitle = reviewDisabled
    ? reviewDisabledReason
    : hasUnsavedManualEdits
      ? 'Save draft before reviewing runtime changes.'
      : !hasSavedDraftForReview && !hasReviewableRuntimeChanges
        ? 'Save an ontology draft before reviewing runtime changes.'
        : undefined;

  return (
    <div
      className="flex min-h-[720px] flex-col overflow-hidden bg-white dark:bg-dark-surface lg:h-[calc(100vh-3.125rem)]"
      data-toolbar-surface="solid"
      data-testid="ontology-editor"
    >
      <div className="border-b border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
        {!headerCollapsed && (
          <div className="px-5 py-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-eliza-red/10 text-eliza-red">
                  <GitBranch className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                    Semantic ontology
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <h1 className="font-title text-[1.7rem] leading-none text-charcoal dark:text-gray-100">
                      Ontology Builder
                    </h1>
                    <Badge variant={readOnly ? 'secondary' : 'brand'}>{readOnly ? 'Active version' : 'Draft editing'}</Badge>
                    <ValidationStatusBadge
                      issues={validationIssues}
                      label={validationStatusLabel}
                      variant={activationIssueCount > 0 ? 'danger' : validationIssues.length > 0 ? 'warning' : 'success'}
                    />
                  </div>
                </div>
              </div>

              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {suggestionReviewAction ?? (!readOnly && onClose && (
                  <Button variant="ghost" size="sm" onClick={onClose}>
                    <X className="h-4 w-4" />
                    Overview
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onDraftWithAI}
                  disabled={aiDraftDisabled || isAiDrafting}
                  title={aiDraftDisabled ? aiDraftDisabledReason : undefined}
                >
                  <Sparkles className={`h-4 w-4 ${isAiDrafting ? 'animate-spin' : ''}`} />
                  {isAiDrafting ? 'Drafting...' : 'Draft with AI'}
                </Button>
                <Button type="button" variant="outline" size="sm" disabled title="Pack testing requires a backend dry-run contract.">
                  <Play className="h-4 w-4" />
                  Test pack
                </Button>
                {onReviewDraft && (
                  <Button
                    type="button"
                    variant={reviewIsNextStep ? 'default' : 'outline'}
                    size="sm"
                    onClick={onReviewDraft}
                    disabled={resolvedReviewDisabled}
                    title={resolvedReviewTitle}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Review changes
                  </Button>
                )}
                <Button size="sm" onClick={saveDraft} disabled={readOnly || isSavingDraft}>
                  {isSavingDraft ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {readOnly ? 'Backend active' : isSavingDraft ? 'Saving...' : 'Save draft'}
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Collapse header"
                  title="Collapse header"
                  onClick={() => setHeaderCollapsed(true)}
                >
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                </Button>
              </div>
            </div>
            <p className="mt-2 max-w-none text-xs leading-5 text-gray-500 dark:text-gray-400 lg:pl-12">
              Model workspace-scoped entities, governed metrics, and approved join paths. Runtime changes only ship after pack review.
            </p>
          </div>
        )}
        {reviewIsNextStep ? (
          <div className="border-t border-emerald-200 bg-emerald-50 px-5 py-3 text-sm text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-900/10 dark:text-emerald-100">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span>Draft saved. Review changes to prepare the runtime version for publishing.</span>
              <Button type="button" size="sm" onClick={onReviewDraft}>
                <CheckCircle2 className="h-4 w-4" />
                Review changes
              </Button>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-3 border-t border-gray-100 px-5 py-2.5 dark:border-dark-border/70 xl:flex-row xl:items-center xl:justify-end">
          <div className={cn('flex shrink-0 items-center gap-2', headerCollapsed ? 'flex-nowrap' : 'flex-wrap')}>
            <div className="hidden items-center gap-1 rounded-full border border-gray-200 bg-white p-1 dark:border-dark-border dark:bg-dark-surface lg:flex">
              <button
                type="button"
                aria-pressed={activeView === 'graph'}
                onClick={() => setActiveView('graph')}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                  activeView === 'graph'
                    ? 'bg-white text-charcoal shadow-sm ring-1 ring-gray-200 dark:bg-dark-surface-2 dark:text-gray-100 dark:ring-dark-border'
                    : 'text-gray-500 hover:bg-white hover:text-charcoal dark:text-gray-400 dark:hover:bg-dark-surface-2 dark:hover:text-gray-100',
                )}
              >
                <GitBranch className="h-3.5 w-3.5" />
                Graph
              </button>
              <button
                type="button"
                aria-pressed={activeView === 'sheet'}
                onClick={() => setActiveView('sheet')}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                  activeView === 'sheet'
                    ? 'bg-white text-charcoal shadow-sm ring-1 ring-gray-200 dark:bg-dark-surface-2 dark:text-gray-100 dark:ring-dark-border'
                    : 'text-gray-500 hover:bg-white hover:text-charcoal dark:text-gray-400 dark:hover:bg-dark-surface-2 dark:hover:text-gray-100',
                )}
              >
                <Columns3 className="h-3.5 w-3.5" />
                Sheet
              </button>
            </div>
            {!readOnly && (
              <>
                <Button variant="secondary" size="sm" onClick={addEntity}>
                  <Plus className="h-4 w-4" />
                  Entity
                </Button>
                <Button variant="outline" size="sm" onClick={addBusinessMeaning}>
                  <Plus className="h-4 w-4" />
                  Meaning
                </Button>
                <Button variant="outline" size="sm" onClick={addMetric}>
                  <Plus className="h-4 w-4" />
                  Metric
                </Button>
                <Button variant="outline" size="sm" onClick={addJoin}>
                  <Plus className="h-4 w-4" />
                  Join
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearDraft}
                  disabled={
                    ontology.entities.length +
                      (ontology.semanticMappings?.length ?? 0) +
                      ontology.metrics.length +
                      ontology.joins.length ===
                    0
                  }
                >
                  <Trash2 className="h-4 w-4" />
                  Clear draft
                </Button>
              </>
            )}
            {headerCollapsed && (
              <>
                <ValidationStatusBadge
                  issues={validationIssues}
                  label={validationStatusLabel}
                  variant={activationIssueCount > 0 ? 'danger' : validationIssues.length > 0 ? 'warning' : 'success'}
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Expand header"
                  title="Expand header"
                  onClick={() => setHeaderCollapsed(false)}
                >
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 border-t border-blue-200 bg-blue-50 px-5 py-2 text-xs text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300">
          <Info className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1">
            Editing ontology draft. Saves stay in draft until review compiles the Runtime Context Pack. {displayedSelectedAssetCount} scoped asset{displayedSelectedAssetCount === 1 ? '' : 's'} available.
          </span>
          <span className="hidden shrink-0 text-[11px] font-semibold uppercase tracking-[0.12em] text-blue-700 dark:text-blue-300 sm:inline">
            {runtimePackLabel} · {readinessLabel ?? 'backend readiness'}
          </span>
        </div>
        {!generationExperience && (isAiDrafting || aiDraftProgressMessage) && (
          <div
            className={cn(
              "border-t px-5 py-2.5",
              isAiDraftFailed
                ? "border-rose-100 bg-rose-50 dark:border-rose-900/50 dark:bg-rose-950/20"
                : "border-purple-100 bg-purple-50 dark:border-purple-900/50 dark:bg-purple-950/20",
            )}
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-2">
                {isAiDraftFailed ? (
                  <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
                ) : (
                  <Sparkles className={cn("h-4 w-4 shrink-0 text-eliza-red", isAiDrafting && "animate-spin")} />
                )}
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-charcoal dark:text-gray-100">
                    {aiDraftProgressMessage ?? 'Starting ontology generation'}
                  </p>
                  <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
                    {aiDraftProgressStage ? aiDraftProgressStage.replace(/_/g, ' ') : 'Backend generation'}
                    {isAiDraftFailed ? ' · failed' : isAiDrafting ? (isAiDraftStreamConnected ? ' · live' : ' · reconnecting') : ' · completed'}
                  </p>
                </div>
              </div>
              <div className="flex w-full items-center gap-2 sm:w-64">
                <Progress
                  className="min-w-0 flex-1"
                  value={aiDraftDisplayPercent ?? 10}
                  size="sm"
                  variant={isAiDraftFailed ? "warning" : "gradient"}
                  indeterminate={!isAiDraftFailed && aiDraftProgressPercent == null}
                />
                <span className="w-10 shrink-0 text-right text-[11px] font-semibold tabular-nums text-gray-500 dark:text-gray-400">
                  {typeof aiDraftDisplayPercent === 'number' ? `${Math.round(aiDraftDisplayPercent)}%` : 'Live'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className={cn(
          'grid min-h-0 flex-1 grid-cols-1',
          generationExperience
            ? 'grid-cols-1'
            : inspectorCollapsed
              ? 'lg:grid-cols-[minmax(0,1fr)_3.5rem]'
              : 'lg:grid-cols-[minmax(0,1fr)_420px]',
        )}
      >
        {generationExperience ? (
          generationExperience
        ) : (
          <>
        <div className="flex min-h-0 flex-col border-r border-gray-200 dark:border-dark-border">
          <div className="min-h-0 flex-1">
            {activeView === 'graph' ? (
              <OntologyGraphCanvas
                ontology={ontology}
                allowlist={allowlist}
                selection={selection}
                onSelect={setSelection}
                onCreateEntity={readOnly ? undefined : addEntity}
                onCreateMetric={readOnly ? undefined : addMetric}
                onCreateJoin={readOnly ? undefined : addJoin}
                onCreateJoinBetweenEntities={readOnly ? undefined : createJoinBetweenEntities}
                layoutStorageKey={layoutStorageKey}
              />
            ) : (
              <OntologySheetView
                ontology={ontology}
                allowlist={allowlist}
                selection={selection}
                onSelect={setSelection}
              />
            )}
          </div>
        </div>

        <aside
          className={cn(
            'flex min-h-0 flex-col bg-white dark:bg-dark-surface',
            inspectorCollapsed && 'items-center',
          )}
          data-testid="ontology-inspector-rail"
        >
          {inspectorCollapsed ? (
            <CollapsedInspectorRail
              issueCount={validationIssues.length}
              hasSelection={selection !== null}
              onExpand={() => setInspectorCollapsed(false)}
            />
          ) : (
            <>
          {selection ? (
            <>
              <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-dark-border">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    Inspector
                  </p>
                  <h3 className="mt-1 text-sm font-semibold text-charcoal dark:text-gray-100">
                    {selection.kind === 'entity' && selectedEntity?.name}
                    {selection.kind === 'businessMeaning' && selectedBusinessMeaning?.label}
                    {selection.kind === 'metric' && selectedMetric?.name}
                    {selection.kind === 'join' && selectedJoin && `${entityById.get(selectedJoin.leftEntityId)?.name ?? 'Entity'} to ${entityById.get(selectedJoin.rightEntityId)?.name ?? 'Entity'}`}
                  </h3>
                </div>
                <div className="flex items-center gap-1">
                  {!readOnly && (
                    <Button variant="ghost" size="icon-sm" aria-label="Delete selected ontology item" onClick={deleteSelected}>
                      <Trash2 className="h-4 w-4 text-gray-500" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Collapse inspector"
                    title="Collapse inspector"
                    onClick={() => setInspectorCollapsed(true)}
                  >
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                  </Button>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-5">
                {selectedEntity && (
                  <EntityInspector
                    entity={selectedEntity}
                    allowlist={allowlist}
                    readOnly={readOnly}
                    onChange={(patch) => updateEntity(selectedEntity.id, patch)}
                    onAddSynonym={(value) => addSynonym(selectedEntity, value)}
                    onRemoveSynonym={(synonym) =>
                      updateEntity(selectedEntity.id, {
                        synonyms: selectedEntity.synonyms.filter((existing) => existing !== synonym),
                      })
                    }
                  />
                )}
                {selectedBusinessMeaning && (
                  <BusinessMeaningInspector
                    mapping={selectedBusinessMeaning}
                    entities={ontology.entities}
                    allowlist={allowlist}
                    readOnly={readOnly}
                    onChange={(patch) => updateBusinessMeaning(selectedBusinessMeaning.id, patch)}
                    onAddAlias={(value) => addBusinessMeaningAlias(selectedBusinessMeaning, value)}
                    onRemoveAlias={(alias) =>
                      updateBusinessMeaning(selectedBusinessMeaning.id, {
                        aliases: selectedBusinessMeaning.aliases.filter((existing) => existing !== alias),
                      })
                    }
                  />
                )}
                {selectedMetric && (
                  <MetricInspector
                    metric={selectedMetric}
                    entities={ontology.entities}
                    semanticMappings={ontology.semanticMappings ?? []}
                    readOnly={readOnly}
                    onChange={(patch) => updateMetric(selectedMetric.id, patch)}
                  />
                )}
                {selectedJoin && (
                  <JoinInspector
                    join={selectedJoin}
                    entities={ontology.entities}
                    entityById={entityById}
                    allowlist={allowlist}
                    readOnly={readOnly}
                    onChange={(patch) => updateJoin(selectedJoin.id, patch)}
                  />
                )}
              </div>
            </>
          ) : (
            <EmptyInspectorState
              readOnly={readOnly}
              onCreateEntity={addEntity}
              onCreateBusinessMeaning={addBusinessMeaning}
              onCreateMetric={addMetric}
              onCreateJoin={addJoin}
              onCollapse={() => setInspectorCollapsed(true)}
            />
          )}
            </>
          )}
        </aside>
          </>
        )}
      </div>
    </div>
  );
}

function OntologySheetView({
  ontology,
  allowlist,
  selection,
  onSelect,
}: {
  ontology: WorkspaceOntology;
  allowlist: StructuredAssetAllowlistRow[];
  selection: Selection;
  onSelect: (selection: Selection) => void;
}) {
  const [activeTab, setActiveTab] = useState<OntologySheetTab>('entities');
  const entityById = useMemo(
    () => new Map(ontology.entities.map((entity) => [entity.id, entity])),
    [ontology.entities],
  );
  const metricsByEntity = useMemo(() => {
    const byEntity = new Map<string, OntologyMetric[]>();
    ontology.metrics.forEach((metric) => {
      byEntity.set(metric.entityId, [...(byEntity.get(metric.entityId) ?? []), metric]);
    });
    return byEntity;
  }, [ontology.metrics]);
  const mappingsByEntity = useMemo(() => {
    const byEntity = new Map<string, OntologySemanticMapping[]>();
    (ontology.semanticMappings ?? []).forEach((mapping) => {
      byEntity.set(mapping.entityId, [...(byEntity.get(mapping.entityId) ?? []), mapping]);
    });
    return byEntity;
  }, [ontology.semanticMappings]);
  const joinsByEntity = useMemo(() => {
    const byEntity = new Map<string, OntologyJoinPath[]>();
    ontology.joins.forEach((join) => {
      byEntity.set(join.leftEntityId, [...(byEntity.get(join.leftEntityId) ?? []), join]);
      byEntity.set(join.rightEntityId, [...(byEntity.get(join.rightEntityId) ?? []), join]);
    });
    return byEntity;
  }, [ontology.joins]);
  const catalogLinkRows = useMemo(
    () => buildCatalogLinkRows(ontology, allowlist, entityById),
    [allowlist, entityById, ontology],
  );
  const tabs: Array<{ id: OntologySheetTab; label: string; count: number }> = [
    { id: 'entities', label: 'Entities', count: ontology.entities.length },
    { id: 'businessMeanings', label: 'Business meanings', count: ontology.semanticMappings?.length ?? 0 },
    { id: 'metrics', label: 'Metrics', count: ontology.metrics.length },
    { id: 'relationships', label: 'Relationships', count: ontology.joins.length },
    { id: 'catalogLinks', label: 'Catalog links', count: catalogLinkRows.length },
  ];

  return (
    <div className="h-full min-h-[640px] overflow-auto bg-gray-50 p-5 dark:bg-dark-bg/30" data-testid="ontology-sheet-view">
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
              <Table2 className="h-4 w-4" />
              Sheet view
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Review logical ontology objects beside metadata coverage and runtime evidence.
            </p>
          </div>
          <Badge variant="secondary">
            {ontology.entities.length} entities · {ontology.semanticMappings?.length ?? 0} business meanings · {ontology.metrics.length} metrics · {ontology.joins.length} relationships
          </Badge>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2 dark:border-dark-border" role="tablist" aria-label="Ontology sheet sections">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'inline-flex h-8 items-center gap-2 rounded-md px-3 text-xs font-medium transition-colors',
                activeTab === tab.id
                  ? 'bg-charcoal text-white dark:bg-gray-100 dark:text-charcoal'
                  : 'bg-white text-gray-600 hover:bg-gray-100 dark:bg-dark-surface dark:text-gray-300 dark:hover:bg-dark-surface-2',
              )}
            >
              {tab.label}
              <span className="rounded-full bg-black/10 px-1.5 py-0.5 text-[10px] dark:bg-white/20">{tab.count}</span>
            </button>
          ))}
        </div>

        {activeTab === 'entities' && (
          <SheetSection title="Entities" description="Logical business things. Each should point at the catalog asset that physically backs it when one exists.">
            <OntologyTable
              headers={['Name', 'Primary asset', 'Source bindings', 'Catalog status', 'Coverage', 'Description']}
              emptyLabel="No entities yet."
            >
              {ontology.entities.map((entity) => {
                const status = getOntologyCatalogLinkStatus(entity, allowlist);
                return (
                  <tr
                    key={entity.id}
                    onClick={() => onSelect({ kind: 'entity', id: entity.id })}
                    className={cn(
                      'cursor-pointer border-t border-gray-100 transition-colors hover:bg-gray-50 dark:border-dark-border dark:hover:bg-dark-surface-2',
                      selection?.kind === 'entity' && selection.id === entity.id && 'bg-eliza-red/5 dark:bg-eliza-red/10',
                    )}
                  >
                    <td className="px-3 py-3">
                      <p className="font-semibold text-charcoal dark:text-gray-100">{entity.name || 'Unnamed entity'}</p>
                      <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">{entity.synonyms.length} aliases</p>
                    </td>
                    <td className="px-3 py-3">
                      <p className="max-w-[18rem] truncate font-mono text-[11px] text-gray-500 dark:text-gray-400">
                        {catalogAssetLabel(entity, allowlist)}
                      </p>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">
                      {entity.sourceBindings?.length ?? (entity.physicalTable ? 1 : 0)}
                    </td>
                    <td className="px-3 py-3">
                      <CatalogStatusBadge status={status} />
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">
                      {mappingsByEntity.get(entity.id)?.length ?? 0} meanings · {metricsByEntity.get(entity.id)?.length ?? 0} metrics · {joinsByEntity.get(entity.id)?.length ?? 0} relations
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">
                      {entity.description ? 'Ready' : 'Missing'}
                    </td>
                  </tr>
                );
              })}
            </OntologyTable>
          </SheetSection>
        )}

        {activeTab === 'businessMeanings' && (
          <SheetSection title="Business meanings" description="Column-level business interpretations that can feed metrics, filters, and generated SQL constraints. Catalog column facts remain referenced evidence.">
            <OntologyTable
              headers={['Label', 'Entity', 'Catalog column ref', 'Role', 'Aliases', 'Description']}
              emptyLabel="No business meanings yet."
            >
              {(ontology.semanticMappings ?? []).map((mapping) => (
                <tr
                  key={mapping.id}
                  onClick={() => onSelect({ kind: 'businessMeaning', id: mapping.id })}
                  className={cn(
                    'cursor-pointer border-t border-gray-100 transition-colors hover:bg-gray-50 dark:border-dark-border dark:hover:bg-dark-surface-2',
                    selection?.kind === 'businessMeaning' && selection.id === mapping.id && 'bg-eliza-red/5 dark:bg-eliza-red/10',
                  )}
                >
                  <td className="px-3 py-3">
                    <p className="font-semibold text-charcoal dark:text-gray-100">{mapping.label}</p>
                    <p className="mt-0.5 font-mono text-[11px] text-gray-500 dark:text-gray-400">{mapping.conceptKey}</p>
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">{entityById.get(mapping.entityId)?.name ?? 'Missing entity'}</td>
                  <td className="px-3 py-3 font-mono text-[11px] text-gray-500 dark:text-gray-400">
                    {mapping.physicalTable || 'No table'} · column {mapping.columnId}
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">{mapping.valueKind ?? mapping.defaultAggregation ?? 'Unclassified'}</td>
                  <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">{mapping.aliases.length}</td>
                  <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">
                    {mapping.description ? 'Ready' : 'Missing'}
                  </td>
                </tr>
              ))}
            </OntologyTable>
          </SheetSection>
        )}

        {activeTab === 'metrics' && (
          <SheetSection title="Metrics" description="Governed measures that should reference logical entities and catalog-backed columns.">
            <OntologyTable headers={['Name', 'Entity', 'Expression', 'Primary column ref', 'Semantic inputs', 'Description']} emptyLabel="No metrics yet.">
              {ontology.metrics.map((metric) => (
                <tr
                  key={metric.id}
                  onClick={() => onSelect({ kind: 'metric', id: metric.id })}
                  className={cn(
                    'cursor-pointer border-t border-gray-100 transition-colors hover:bg-gray-50 dark:border-dark-border dark:hover:bg-dark-surface-2',
                    selection?.kind === 'metric' && selection.id === metric.id && 'bg-eliza-red/5 dark:bg-eliza-red/10',
                  )}
                >
                  <td className="px-3 py-3 font-semibold text-charcoal dark:text-gray-100">{metric.name}</td>
                  <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">{entityById.get(metric.entityId)?.name ?? 'Missing entity'}</td>
                  <td className="max-w-[22rem] truncate px-3 py-3 font-mono text-[11px] text-gray-500 dark:text-gray-400">{metric.expression || 'Missing expression'}</td>
                  <td className="px-3 py-3 font-mono text-[11px] text-gray-500 dark:text-gray-400">{metric.primaryColumnId ?? 'None'}</td>
                  <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">
                    {metricSemanticInputCount(metric, ontology.semanticMappings ?? [])}
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">{metric.description ? 'Ready' : 'Missing'}</td>
                </tr>
              ))}
            </OntologyTable>
          </SheetSection>
        )}

        {activeTab === 'relationships' && (
          <SheetSection title="Relationships" description="Logical entity links. Semantic-only relationships are visible in ontology context but are not SQL-traversable until join evidence is validated.">
            <OntologyTable headers={['Meaning', 'Logical route', 'Kind', 'Join evidence', 'Cardinality', 'Status']} emptyLabel="No relationships yet.">
              {ontology.joins.map((join) => (
                <tr
                  key={join.id}
                  onClick={() => onSelect({ kind: 'join', id: join.id })}
                  className={cn(
                    'cursor-pointer border-t border-gray-100 transition-colors hover:bg-gray-50 dark:border-dark-border dark:hover:bg-dark-surface-2',
                    selection?.kind === 'join' && selection.id === join.id && 'bg-eliza-red/5 dark:bg-eliza-red/10',
                  )}
                >
                  <td className="px-3 py-3 font-semibold text-charcoal dark:text-gray-100">
                    {join.predicate || join.name || 'Relationship needs predicate'}
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">
                    {entityById.get(join.leftEntityId)?.name ?? 'Missing entity'} {'->'} {entityById.get(join.rightEntityId)?.name ?? 'Missing entity'}
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">{relationshipKindLabel(join)}</td>
                  <td className="max-w-[28rem] truncate px-3 py-3 text-xs text-gray-600 dark:text-gray-300">
                    {relationshipEvidenceLabel(join)}
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">{join.cardinality ?? 'Unspecified'}</td>
                  <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">
                    {relationshipReadinessLabel(join)}
                  </td>
                </tr>
              ))}
            </OntologyTable>
          </SheetSection>
        )}

        {activeTab === 'catalogLinks' && (
          <SheetSection title="Catalog Links" description="Read-only coverage audit. Catalog-owned descriptions, aliases, and sample values are referenced as evidence and are not copied into ontology rows.">
            <OntologyTable headers={['Ontology artifact', 'Type', 'Physical ref', 'Catalog status', 'Action needed']} emptyLabel="No catalog link references yet.">
              {catalogLinkRows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => row.selection && onSelect(row.selection)}
                  className={cn(
                    'border-t border-gray-100 transition-colors dark:border-dark-border',
                    row.selection && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-surface-2',
                    row.selection && selection?.kind === row.selection.kind && selection.id === row.selection.id && 'bg-eliza-red/5 dark:bg-eliza-red/10',
                  )}
                >
                  <td className="px-3 py-3 font-semibold text-charcoal dark:text-gray-100">{row.artifactLabel}</td>
                  <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">{row.artifactType}</td>
                  <td className="max-w-[24rem] truncate px-3 py-3 font-mono text-[11px] text-gray-500 dark:text-gray-400">{row.physicalRef}</td>
                  <td className="px-3 py-3"><CatalogStatusBadge status={row.status} /></td>
                  <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">{row.actionNeeded}</td>
                </tr>
              ))}
            </OntologyTable>
          </SheetSection>
        )}
      </div>
    </div>
  );
}

function SheetSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
      <div className="border-b border-gray-100 px-4 py-3 dark:border-dark-border">
        <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">{title}</h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
      </div>
      {children}
    </section>
  );
}

function OntologyTable({
  headers,
  emptyLabel,
  children,
}: {
  headers: string[];
  emptyLabel: string;
  children: React.ReactNode;
}) {
  const rows = React.Children.toArray(children).filter(Boolean);
  if (rows.length === 0) {
    return <div className="px-4 py-5 text-sm text-gray-500 dark:text-gray-400">{emptyLabel}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] text-left">
        <thead>
          <tr className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
            {headers.map((header) => (
              <th key={header} className="px-3 py-2">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

function EntityInspector({
  entity,
  allowlist,
  readOnly,
  onChange,
  onAddSynonym,
  onRemoveSynonym,
}: {
  entity: OntologyEntity;
  allowlist: StructuredAssetAllowlistRow[];
  readOnly: boolean;
  onChange: (patch: Partial<OntologyEntity>) => void;
  onAddSynonym: (value: string) => void;
  onRemoveSynonym: (value: string) => void;
}) {
  const [synonymDraft, setSynonymDraft] = useState('');
  const catalogStatus = getOntologyCatalogLinkStatus(entity, allowlist);

  return (
    <div className="space-y-5">
      <InspectorField label="Business name">
        <Input value={entity.name} onChange={(event) => onChange({ name: event.target.value })} disabled={readOnly} />
      </InspectorField>
      <CatalogLinkPanel entity={entity} allowlist={allowlist} status={catalogStatus} />
      <CatalogEvidenceSummary
        title="Referenced catalog evidence"
        rows={catalogEvidenceRowsForEntity(entity, allowlist)}
        emptyText="No catalog evidence is available for this entity until it is linked to a scoped asset."
      />
      <InspectorField label="Physical warehouse asset">
        <select
          value={entity.physicalTable}
          onChange={(event) => onChange({ physicalTable: event.target.value })}
          disabled={readOnly}
          className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
        >
          <option value="">Select a scoped table or view</option>
          {allowlist.map((row) => (
            <option key={row.id} value={`table:${row.id}`}>
              {row.dataset}.{row.objectName} ({row.objectType})
            </option>
          ))}
        </select>
      </InspectorField>
      <InspectorField label={`Reviewed source bindings (${entity.sourceBindings?.length ?? 0})`}>
        {(entity.sourceBindings?.length ?? 0) === 0 ? (
          <p className="text-xs text-gray-400">
            Saving this entity will create a primary binding from the selected asset.
          </p>
        ) : (
          <div className="space-y-2">
            {(entity.sourceBindings ?? []).map((binding) => {
              const asset = allowlist.find((row) => row.id === binding.tableId);
              return (
                <div
                  key={binding.tableId}
                  className="rounded-md border border-gray-200 bg-gray-50 p-2.5 dark:border-dark-border dark:bg-dark-surface-2"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono text-[11px] text-charcoal dark:text-gray-100">
                      {asset ? `${asset.dataset}.${asset.objectName}` : `table:${binding.tableId}`}
                    </span>
                    <Badge variant={binding.reviewState === 'approved' ? 'success' : 'warning'}>
                      {binding.reviewState}
                    </Badge>
                  </div>
                  <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    {binding.bindingRole || 'source'} · {binding.matchMethod || 'unclassified match'} ·{' '}
                    {Math.round(binding.confidence * 100)}% · {binding.executionMode.replace(/_/g, ' ')}
                  </p>
                  <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    Authority: {binding.sourceAuthority || binding.trustContract?.authorityTier || 'unspecified'} ·{' '}
                    evidence: {binding.matchEvidence.length} · conflict: {binding.conflictState ?? 'none'}
                  </p>
                  {binding.identityColumns.length > 0 ? (
                    <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                      {binding.identityColumns.length} identity field{binding.identityColumns.length === 1 ? '' : 's'}
                    </p>
                  ) : null}
                  {(binding.identityMappings?.length ?? 0) > 0 ? (
                    <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                      {(binding.identityMappings ?? []).map((mapping) => mapping.mappingType.replace(/_/g, ' ')).join(', ')}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </InspectorField>
      <InspectorField label="Description">
        <Textarea
          value={entity.description}
          onChange={(event) => onChange({ description: event.target.value })}
          disabled={readOnly}
          rows={4}
          placeholder="Describe the business meaning and safe query usage."
        />
      </InspectorField>
      <InspectorField label="Synonyms and aliases">
        <div className="flex flex-wrap gap-1.5">
          {entity.synonyms.length === 0 ? (
            <span className="text-xs text-gray-400">No aliases yet.</span>
          ) : (
            entity.synonyms.map((synonym) => (
              <span key={synonym} className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-600 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300">
                {synonym}
                {!readOnly && (
                  <button type="button" aria-label={`Remove ${synonym}`} onClick={() => onRemoveSynonym(synonym)}>
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))
          )}
        </div>
        {!readOnly && (
          <div className="mt-2 flex gap-2">
            <Input
              aria-label="New synonym"
              value={synonymDraft}
              onChange={(event) => setSynonymDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  onAddSynonym(synonymDraft);
                  setSynonymDraft('');
                }
              }}
              placeholder="customer, account, buyer"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Add synonym"
              onClick={() => {
                onAddSynonym(synonymDraft);
                setSynonymDraft('');
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}
      </InspectorField>
      <ProvenanceNote text="Workspace ontology owns business meaning. Catalog coverage may enrich descriptions, but does not make the workspace runtime ready." />
    </div>
  );
}

function CatalogLinkPanel({
  entity,
  allowlist,
  status,
}: {
  entity: OntologyEntity;
  allowlist: StructuredAssetAllowlistRow[];
  status: OntologyCatalogLinkStatus;
}) {
  const asset = resolveOntologyEntityAsset(entity, allowlist);

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
            Catalog link
          </p>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
            {catalogLinkDetail(entity, allowlist)}
          </p>
        </div>
        <CatalogStatusBadge status={status} />
      </div>
      {asset ? (
        <div className="mt-2 grid gap-2 text-[11px] text-gray-500 dark:text-gray-400 sm:grid-cols-2">
          <span>{asset.objectType}</span>
          <span>{asset.certified ? 'Certified' : 'Not certified'}</span>
          <span>{asset.rowEstimate ?? 'Rows unknown'}</span>
          <span>{asset.freshness ?? 'Freshness unknown'}</span>
        </div>
      ) : (
        <p className="mt-2 text-[11px] leading-5 text-gray-500 dark:text-gray-400">
          Link this entity to a scoped catalog table or view when a physical backing asset exists.
        </p>
      )}
    </div>
  );
}

type CatalogEvidenceRow = {
  label: string;
  value: string;
};

function catalogEvidenceRowsForEntity(
  entity: OntologyEntity,
  allowlist: StructuredAssetAllowlistRow[],
  prefix = 'Mapped asset',
): CatalogEvidenceRow[] {
  const asset = resolveOntologyEntityAsset(entity, allowlist);
  if (!asset) return [];
  return [
    { label: prefix, value: `${asset.dataset}.${asset.objectName}` },
    { label: 'Object type', value: asset.objectType },
    { label: 'Rows', value: asset.rowEstimate || 'Rows unknown' },
    { label: 'Freshness', value: asset.freshness || 'Freshness unknown' },
    { label: 'Certification', value: asset.certified ? 'Certified' : 'Not certified' },
    { label: 'Catalog resolver', value: asset.catalogBadge },
  ];
}

function catalogEvidenceRowsForMetric(
  metric: OntologyMetric,
  linkedMappings: OntologySemanticMapping[],
): CatalogEvidenceRow[] {
  const rows: CatalogEvidenceRow[] = [];
  if (metric.primaryColumnId) {
    rows.push({ label: 'Primary column ref', value: metric.primaryColumnId });
  }
  linkedMappings.slice(0, 4).forEach((mapping) => {
    rows.push({
      label: mapping.label,
      value: `${mapping.physicalTable || 'No table'} · column ${mapping.columnId}`,
    });
  });
  return rows;
}

function catalogEvidenceRowsForBusinessMeaning(
  mapping: OntologySemanticMapping,
  allowlist: StructuredAssetAllowlistRow[],
): CatalogEvidenceRow[] {
  const status = conceptCatalogStatus(mapping, allowlist);
  return [
    { label: 'Catalog status', value: ontologyCatalogLinkStatusLabel(status) },
    { label: 'Mapped asset', value: mapping.physicalTable || 'No table selected' },
    { label: 'Column ref', value: mapping.columnId || 'No column selected' },
    { label: 'Role', value: mapping.valueKind || 'Unclassified' },
    { label: 'Default aggregation', value: mapping.defaultAggregation || 'None' },
  ];
}

function CatalogEvidenceSummary({
  title,
  rows,
  emptyText,
}: {
  title: string;
  rows: CatalogEvidenceRow[];
  emptyText: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-dark-border dark:bg-dark-surface">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
          {title}
        </p>
        <Badge variant="secondary">Read only</Badge>
      </div>
      {rows.length === 0 ? (
        <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">{emptyText}</p>
      ) : (
        <dl className="mt-2 grid gap-2 text-xs">
          {rows.map((row, index) => (
            <div key={`${row.label}:${row.value}:${index}`} className="grid grid-cols-[7rem_1fr] gap-2">
              <dt className="text-gray-400 dark:text-gray-500">{row.label}</dt>
              <dd className="truncate text-gray-600 dark:text-gray-300">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <p className="mt-3 border-t border-gray-100 pt-2 text-[11px] leading-5 text-gray-500 dark:border-dark-border dark:text-gray-400">
        Catalog descriptions, aliases, and sample values are referenced by runtime context packs; ontology saves do not copy those field facts.
      </p>
    </div>
  );
}

function MetricInspector({
  metric,
  entities,
  semanticMappings,
  readOnly,
  onChange,
}: {
  metric: OntologyMetric;
  entities: OntologyEntity[];
  semanticMappings: OntologySemanticMapping[];
  readOnly: boolean;
  onChange: (patch: Partial<OntologyMetric>) => void;
}) {
  const linkedMappings = semanticMappings.filter(
    (mapping) =>
      mapping.entityId === metric.entityId ||
      (metric.primaryColumnId && mapping.columnId === metric.primaryColumnId) ||
      JSON.stringify(metric.expressionMetadata ?? {}).includes(mapping.conceptKey),
  );

  return (
    <div className="space-y-5">
      <InspectorField label="Metric name">
        <Input value={metric.name} onChange={(event) => onChange({ name: event.target.value })} disabled={readOnly} />
      </InspectorField>
      <InspectorField label="Parent entity">
        <select
          value={metric.entityId}
          onChange={(event) => onChange({ entityId: event.target.value })}
          disabled={readOnly}
          className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
        >
          <option value="">Select an entity</option>
          {entities.map((entity) => (
            <option key={entity.id} value={entity.id}>
              {entity.name}
            </option>
          ))}
        </select>
      </InspectorField>
      <InspectorField label="SQL expression">
        <Textarea
          value={metric.expression}
          onChange={(event) => onChange({ expression: event.target.value })}
          disabled={readOnly}
          rows={4}
          className="font-mono text-xs"
          placeholder="SUM(amount)"
        />
      </InspectorField>
      <InspectorField label="Description">
        <Textarea
          value={metric.description}
          onChange={(event) => onChange({ description: event.target.value })}
          disabled={readOnly}
          rows={3}
          placeholder="Explain what the metric measures and when it should be used."
        />
      </InspectorField>
      <InspectorField label="Structured expression metadata">
        <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2">
          {linkedMappings.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              No business meaning inputs are linked to this metric yet.
            </p>
          ) : (
            linkedMappings.map((mapping) => (
              <div key={mapping.id} className="rounded-md border border-gray-200 bg-white p-2 dark:border-dark-border dark:bg-dark-surface">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-xs font-semibold text-charcoal dark:text-gray-100">{mapping.label}</p>
                    <p className="mt-0.5 truncate font-mono text-[11px] text-gray-500 dark:text-gray-400">
                      {mapping.conceptKey} · column {mapping.columnId}
                    </p>
                  </div>
                  {mapping.defaultAggregation ? (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-dark-surface-2 dark:text-gray-300">
                      {mapping.defaultAggregation}
                    </span>
                  ) : null}
                </div>
                {mapping.aliases.length > 0 ? (
                  <p className="mt-1 line-clamp-1 text-[11px] text-gray-500 dark:text-gray-400">
                    Aliases: {mapping.aliases.join(', ')}
                  </p>
                ) : null}
              </div>
            ))
          )}
          {metric.expressionMetadata ? (
            <pre className="max-h-36 overflow-auto rounded-md bg-white p-2 font-mono text-[11px] text-gray-600 dark:bg-dark-surface dark:text-gray-300">
              {JSON.stringify(metric.expressionMetadata, null, 2)}
            </pre>
          ) : null}
        </div>
      </InspectorField>
      <CatalogEvidenceSummary
        title="Referenced input evidence"
        rows={catalogEvidenceRowsForMetric(metric, linkedMappings)}
        emptyText="Link this metric to business meaning inputs to show catalog-backed evidence here."
      />
      <ProvenanceNote text="Metric testing and pack activation are backend-owned. Saving here only persists the draft ontology payload." />
    </div>
  );
}

function BusinessMeaningInspector({
  mapping,
  entities,
  allowlist,
  readOnly,
  onChange,
  onAddAlias,
  onRemoveAlias,
}: {
  mapping: OntologySemanticMapping;
  entities: OntologyEntity[];
  allowlist: StructuredAssetAllowlistRow[];
  readOnly: boolean;
  onChange: (patch: Partial<OntologySemanticMapping>) => void;
  onAddAlias: (value: string) => void;
  onRemoveAlias: (value: string) => void;
}) {
  const [aliasDraft, setAliasDraft] = useState('');
  const linkedEntity = entities.find((entity) => entity.id === mapping.entityId);

  return (
    <div className="space-y-5">
      <InspectorField label="Business meaning label">
        <Input value={mapping.label} onChange={(event) => onChange({ label: event.target.value })} disabled={readOnly} />
      </InspectorField>
      <InspectorField label="Compatibility key">
        <Input
          value={mapping.conceptKey}
          onChange={(event) => onChange({ conceptKey: event.target.value })}
          disabled={readOnly}
          className="font-mono text-xs"
          placeholder="entity.field_meaning"
        />
      </InspectorField>
      <InspectorField label="Parent entity">
        <select
          value={mapping.entityId}
          onChange={(event) => {
            const nextEntity = entities.find((entity) => entity.id === event.target.value);
            onChange({
              entityId: event.target.value,
              physicalTable: nextEntity?.physicalTable || mapping.physicalTable,
            });
          }}
          disabled={readOnly}
          className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
        >
          <option value="">Select an entity</option>
          {entities.map((entity) => (
            <option key={entity.id} value={entity.id}>
              {entity.name}
            </option>
          ))}
        </select>
      </InspectorField>
      <InspectorField label="Physical warehouse asset">
        <select
          value={mapping.physicalTable}
          onChange={(event) => onChange({ physicalTable: event.target.value })}
          disabled={readOnly}
          className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
        >
          <option value="">Select a scoped table or view</option>
          {allowlist.map((row) => (
            <option key={row.id} value={`table:${row.id}`}>
              {row.dataset}.{row.objectName} ({row.objectType})
            </option>
          ))}
        </select>
      </InspectorField>
      <InspectorField label="Catalog column ref">
        <Input
          value={mapping.columnId}
          onChange={(event) => onChange({ columnId: event.target.value })}
          disabled={readOnly}
          className="font-mono text-xs"
          placeholder="Column id or name"
        />
      </InspectorField>
      <div className="grid gap-3 sm:grid-cols-2">
        <InspectorField label="Role">
          <select
            value={mapping.valueKind ?? ''}
            onChange={(event) => onChange({ valueKind: event.target.value || null })}
            disabled={readOnly}
            className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
          >
            <option value="">Unclassified</option>
            <option value="identifier">Identifier</option>
            <option value="dimension">Dimension</option>
            <option value="measure">Measure input</option>
            <option value="timestamp">Timestamp</option>
            <option value="status">Status</option>
          </select>
        </InspectorField>
        <InspectorField label="Default aggregation">
          <select
            value={mapping.defaultAggregation ?? ''}
            onChange={(event) => onChange({ defaultAggregation: event.target.value || null })}
            disabled={readOnly}
            className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
          >
            <option value="">None</option>
            <option value="sum">SUM</option>
            <option value="avg">AVG</option>
            <option value="count">COUNT</option>
            <option value="count_distinct">COUNT DISTINCT</option>
            <option value="min">MIN</option>
            <option value="max">MAX</option>
          </select>
        </InspectorField>
      </div>
      <InspectorField label="Description">
        <Textarea
          value={mapping.description}
          onChange={(event) => onChange({ description: event.target.value })}
          disabled={readOnly}
          rows={4}
          placeholder="Describe the business interpretation and safe SQL use for this catalog-backed field."
        />
      </InspectorField>
      <InspectorField label="Aliases">
        <div className="flex flex-wrap gap-1.5">
          {mapping.aliases.length === 0 ? (
            <span className="text-xs text-gray-400">No aliases yet.</span>
          ) : (
            mapping.aliases.map((alias) => (
              <span key={alias} className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-600 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300">
                {alias}
                {!readOnly && (
                  <button type="button" aria-label={`Remove ${alias}`} onClick={() => onRemoveAlias(alias)}>
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))
          )}
        </div>
        {!readOnly && (
          <div className="mt-2 flex gap-2">
            <Input
              aria-label="New business meaning alias"
              value={aliasDraft}
              onChange={(event) => setAliasDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  onAddAlias(aliasDraft);
                  setAliasDraft('');
                }
              }}
              placeholder="stay length, guest spend, trip value"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Add business meaning alias"
              onClick={() => {
                onAddAlias(aliasDraft);
                setAliasDraft('');
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}
      </InspectorField>
      <CatalogEvidenceSummary
        title="Referenced catalog evidence"
        rows={catalogEvidenceRowsForBusinessMeaning(mapping, allowlist)}
        emptyText="Link this business meaning to a scoped catalog column before runtime activation."
      />
      <ProvenanceNote
        text={`Business meanings map catalog columns into workspace language${linkedEntity ? ` for ${linkedEntity.name}` : ''}. Catalog descriptions, aliases, and sample values remain catalog-owned evidence.`}
      />
    </div>
  );
}

function JoinInspector({
  join,
  entities,
  entityById,
  allowlist,
  readOnly,
  onChange,
}: {
  join: OntologyJoinPath;
  entities: OntologyEntity[];
  entityById: Map<string, OntologyEntity>;
  allowlist: StructuredAssetAllowlistRow[];
  readOnly: boolean;
  onChange: (patch: Partial<OntologyJoinPath>) => void;
}) {
  const leftEntity = entityById.get(join.leftEntityId);
  const rightEntity = entityById.get(join.rightEntityId);
  const evidenceStatus = relationshipEvidenceStatus(join);
  const isTraversable = relationshipTraversalAllowed(join);
  const readinessLabel = relationshipReadinessLabel(join);
  const readinessDescription = relationshipReadinessDescription(join);

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
              Relationship contract
            </p>
            <p className="mt-1 text-xs leading-5 text-gray-600 dark:text-gray-300">
              Predicate fields define the business edge. Join evidence only controls whether SQL traversal is allowed.
            </p>
          </div>
          <Badge variant={relationshipEvidenceTone(join)}>
            {readinessLabel}
          </Badge>
        </div>
        <div className="mt-2 grid gap-1 font-mono text-[11px] text-gray-500 dark:text-gray-400">
          <span>{leftEntity ? catalogAssetLabel(leftEntity, allowlist) : 'Missing left entity'}</span>
          <span>{rightEntity ? catalogAssetLabel(rightEntity, allowlist) : 'Missing right entity'}</span>
        </div>
      </div>
      <InspectorField label="Predicate">
        <Input
          value={join.predicate ?? ''}
          onChange={(event) => onChange({ predicate: event.target.value, name: event.target.value || join.name })}
          disabled={readOnly}
          placeholder="books, contains, summarizes, belongs to"
        />
      </InspectorField>
      <div className="grid gap-3 sm:grid-cols-2">
        <InspectorField label="Relationship kind">
          <select
            value={relationshipKind(join)}
            onChange={(event) => onChange({ relationshipKind: event.target.value })}
            disabled={readOnly}
            className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
          >
            <option value="semantic_only">Semantic only</option>
            <option value="join_backed">Join-backed</option>
            <option value="computed">Computed</option>
          </select>
        </InspectorField>
        <InspectorField label="Optionality">
          <select
            value={join.optionality ?? 'unknown'}
            onChange={(event) => onChange({ optionality: event.target.value })}
            disabled={readOnly}
            className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
          >
            <option value="unknown">Unknown</option>
            <option value="required">Required</option>
            <option value="optional">Optional</option>
          </select>
        </InspectorField>
      </div>
      <InspectorField label="Inverse predicate">
        <Input
          value={join.inversePredicate ?? ''}
          onChange={(event) => onChange({ inversePredicate: event.target.value || null })}
          disabled={readOnly}
          placeholder="is booked by, is contained by, rolls up from"
        />
      </InspectorField>
      <div className="grid gap-3 sm:grid-cols-2">
        <InspectorField label="Source role">
          <Input
            value={join.sourceRole ?? ''}
            onChange={(event) => onChange({ sourceRole: event.target.value || null })}
            disabled={readOnly}
            placeholder="guest, session, source"
          />
        </InspectorField>
        <InspectorField label="Target role">
          <Input
            value={join.targetRole ?? ''}
            onChange={(event) => onChange({ targetRole: event.target.value || null })}
            disabled={readOnly}
            placeholder="trip, summary, target"
          />
        </InspectorField>
      </div>
      <InspectorField label="Cardinality">
        <select
          value={join.cardinality ?? ''}
          onChange={(event) => onChange({ cardinality: event.target.value || null })}
          disabled={readOnly}
          className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
        >
          <option value="">Unspecified</option>
          <option value="one_to_one">One to one</option>
          <option value="one_to_many">One to many</option>
          <option value="many_to_one">Many to one</option>
          <option value="many_to_many">Many to many</option>
        </select>
      </InspectorField>
      <CatalogEvidenceSummary
        title="Relationship evidence state"
        rows={[
          ...(leftEntity ? catalogEvidenceRowsForEntity(leftEntity, allowlist, 'Left entity') : []),
          ...(rightEntity ? catalogEvidenceRowsForEntity(rightEntity, allowlist, 'Right entity') : []),
          {
            label: 'Join evidence',
            value: relationshipEvidenceLabel(join),
          },
          {
            label: 'Evidence status',
            value: evidenceStatus,
          },
          {
            label: 'Readiness',
            value: readinessLabel,
          },
          {
            label: 'SQL traversal',
            value: isTraversable ? 'Allowed' : 'Not allowed',
          },
          {
            label: 'Structured conditions',
            value: Array.isArray(join.joinConditions) && join.joinConditions.length > 0 ? `${join.joinConditions.length}` : 'None',
          },
          {
            label: 'Cardinality',
            value: join.cardinality || 'Not declared',
          },
        ]}
        emptyText="No endpoint evidence is available until both entities are linked."
      />
      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
        <InspectorField label="Left entity">
          <select
            value={join.leftEntityId}
            onChange={(event) => onChange({ leftEntityId: event.target.value })}
            disabled={readOnly}
            className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
          >
            {entities.map((entity) => (
              <option key={entity.id} value={entity.id}>
                {entity.name}
              </option>
            ))}
          </select>
        </InspectorField>
        <Link2 className="mb-3 h-4 w-4 text-gray-400" />
        <InspectorField label="Right entity">
          <select
            value={join.rightEntityId}
            onChange={(event) => onChange({ rightEntityId: event.target.value })}
            disabled={readOnly}
            className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
          >
            {entities.map((entity) => (
              <option key={entity.id} value={entity.id}>
                {entity.name}
              </option>
            ))}
          </select>
        </InspectorField>
      </div>
      <InspectorField label="Join condition evidence">
        <Textarea
          value={join.joinCondition}
          onChange={(event) => onChange({
            joinCondition: event.target.value,
            relationshipKind: event.target.value.trim() ? relationshipKind(join) === 'semantic_only' ? 'join_backed' : relationshipKind(join) : relationshipKind(join),
          })}
          disabled={readOnly}
          rows={4}
          className="font-mono text-xs"
          placeholder={`${entityById.get(join.leftEntityId)?.physicalTable ?? 'left'}.id = ${entityById.get(join.rightEntityId)?.physicalTable ?? 'right'}.id`}
        />
      </InspectorField>
      <InspectorField label="Description">
        <Textarea
          value={join.description}
          onChange={(event) => onChange({ description: event.target.value })}
          disabled={readOnly}
          rows={3}
          placeholder="Explain the business relationship and, when join-backed, when its SQL evidence is safe."
        />
      </InspectorField>
      <ProvenanceNote text={readinessDescription} />
    </div>
  );
}

function InspectorField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-gray-600 dark:text-gray-300">{label}</Label>
      {children}
    </div>
  );
}

function ProvenanceNote({ text }: { text: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
      {text}
    </div>
  );
}

function CatalogStatusBadge({ status }: { status: OntologyCatalogLinkStatus }) {
  return (
    <Badge
      variant={
        status === 'linked'
          ? 'success'
          : status === 'partial'
            ? 'warning'
            : status === 'invalid'
              ? 'danger'
              : 'secondary'
      }
    >
      {ontologyCatalogLinkStatusLabel(status)}
    </Badge>
  );
}

function catalogAssetLabel(entity: OntologyEntity, allowlist: StructuredAssetAllowlistRow[]): string {
  const asset = resolveOntologyEntityAsset(entity, allowlist);
  if (asset) return `${asset.dataset}.${asset.objectName}`;
  return entity.physicalTable || 'No catalog asset linked';
}

function catalogLinkDetail(entity: OntologyEntity, allowlist: StructuredAssetAllowlistRow[]): string {
  const status = getOntologyCatalogLinkStatus(entity, allowlist);
  if (status === 'linked') {
    return `${catalogAssetLabel(entity, allowlist)} is matched in the scoped catalog.`;
  }
  if (status === 'partial') {
    return `${catalogAssetLabel(entity, allowlist)} needs catalog review before activation.`;
  }
  if (status === 'invalid') {
    return `${entity.physicalTable} does not match the current scoped catalog allowlist.`;
  }
  return 'No physical catalog asset is linked yet.';
}

function relationshipKind(join: OntologyJoinPath): string {
  return join.relationshipKind || (join.joinCondition.trim() ? 'join_backed' : 'semantic_only');
}

function relationshipKindLabel(join: OntologyJoinPath): string {
  const kind = relationshipKind(join);
  if (kind === 'semantic_only') return 'Semantic only';
  if (kind === 'join_backed') return 'Join-backed';
  if (kind === 'computed') return 'Computed';
  return kind;
}

function relationshipEvidenceStatus(join: OntologyJoinPath): 'validated' | 'draft' | 'failed' | 'legacy_sql' | 'missing' {
  const status = join.joinEvidenceStatus?.toLowerCase();
  // `traversal_allowed` is the backend's final, evidence-aware decision. The
  // overview contract can still expose the underlying catalog status as
  // `metadata_compatible` even after persisted physical evidence made the
  // route executable. Do not turn that contract detail into a false UI
  // blocker.
  if (relationshipKind(join) === 'join_backed' && join.traversalAllowed === true) {
    return 'validated';
  }
  if (status === 'validated' || status === 'physically_validated' || status === 'steward_exception') {
    return 'validated';
  }
  if (status === 'draft' || status === 'failed' || status === 'legacy_sql') {
    return status;
  }
  if (Array.isArray(join.joinConditions) && join.joinConditions.length > 0) {
    const statuses = new Set(
      join.joinConditions
        .map((condition) => condition.validation_status ? String(condition.validation_status).toLowerCase() : '')
        .filter(Boolean),
    );
    if (statuses.size > 0 && Array.from(statuses).every((item) => item === 'validated')) return 'validated';
    if (statuses.has('failed')) return 'failed';
    if (statuses.size === 0 && join.joinCondition.trim()) return 'legacy_sql';
    return 'draft';
  }
  return join.joinCondition.trim() ? 'legacy_sql' : 'missing';
}

function relationshipTraversalAllowed(join: OntologyJoinPath): boolean {
  const status = relationshipEvidenceStatus(join);
  return (
    relationshipKind(join) === 'join_backed'
    && status === 'validated'
    && join.traversalAllowed !== false
  );
}

function relationshipEvidenceLabel(join: OntologyJoinPath): string {
  const status = relationshipEvidenceStatus(join);
  if (relationshipKind(join) === 'semantic_only' && status === 'missing') return 'Semantic-only, no SQL evidence';
  if (status === 'validated') return 'Validated structured evidence';
  if (status === 'draft') return 'Unvalidated structured evidence';
  if (status === 'failed') return 'Failed evidence';
  if (status === 'legacy_sql') return join.joinCondition || 'Legacy SQL evidence';
  return 'Missing join evidence';
}

function relationshipReadinessLabel(join: OntologyJoinPath): string {
  if (relationshipTraversalAllowed(join)) return 'SQL traversable';
  if (relationshipKind(join) === 'semantic_only') return 'Semantic only';
  const status = relationshipEvidenceStatus(join);
  if (status === 'draft') return 'Needs validation';
  if (status === 'failed') return 'Evidence failed';
  if (status === 'missing') return 'Missing evidence';
  return 'Not SQL ready';
}

function relationshipReadinessDescription(join: OntologyJoinPath): string {
  const kind = relationshipKind(join);
  const status = relationshipEvidenceStatus(join);
  if (kind === 'semantic_only') {
    return 'This relationship is visible in ontology context for planning and explanation. It is not SQL-traversable unless it is converted to a join-backed relationship with validated evidence.';
  }
  if (kind === 'computed') {
    return 'This computed relationship can describe derived business logic, but runtime SQL traversal requires an explicit implementation or validated join evidence.';
  }
  if (relationshipTraversalAllowed(join)) {
    return 'Validated join evidence is available. Runtime SQL generation can traverse this relationship.';
  }
  if (status === 'legacy_sql') {
    return 'Raw SQL join text is metadata only. Runtime traversal remains blocked until bounded physical evidence is validated.';
  }
  if (status === 'draft') {
    return 'Join evidence exists, but it has not been validated against catalog/schema rules yet. Runtime SQL traversal remains blocked until validation approves it.';
  }
  if (status === 'failed') {
    return 'Join evidence is present but failed validation. Runtime SQL traversal is blocked until the evidence is corrected and validated.';
  }
  if (status === 'missing') {
    return 'This join-backed relationship has no join evidence. Add and validate join evidence before runtime SQL generation can traverse it.';
  }
  return 'This relationship is not SQL-ready yet. Validate its join evidence before allowing runtime SQL traversal.';
}

function relationshipEvidenceTone(join: OntologyJoinPath): BadgeProps['variant'] {
  if (relationshipTraversalAllowed(join)) return 'success';
  if (relationshipEvidenceStatus(join) === 'failed') return 'danger';
  if (relationshipKind(join) === 'semantic_only') return 'secondary';
  return 'warning';
}

type CatalogLinkAuditRow = {
  id: string;
  artifactLabel: string;
  artifactType: string;
  physicalRef: string;
  status: OntologyCatalogLinkStatus;
  actionNeeded: string;
  selection: Selection;
};

function conceptCatalogStatus(mapping: OntologySemanticMapping, allowlist: StructuredAssetAllowlistRow[]): OntologyCatalogLinkStatus {
  const tableRef = mapping.physicalTable;
  if (!tableRef) return 'unlinked';
  const ref = tableRef.startsWith('table:') ? tableRef.slice('table:'.length) : tableRef;
  const asset =
    allowlist.find((row) => row.id === ref) ??
    allowlist.find((row) => `${row.dataset}.${row.objectName}` === ref);
  if (!asset) return 'invalid';
  if (mapping.catalogAssetId || asset.catalogBadge === 'matched') return 'linked';
  if (asset.catalogBadge === 'ambiguous' || asset.catalogBadge === 'stale') return 'partial';
  return 'partial';
}

function catalogActionForStatus(status: OntologyCatalogLinkStatus, kind: string): string {
  if (status === 'linked') return 'No action needed';
  if (status === 'partial') return `Review ${kind} catalog evidence`;
  if (status === 'invalid') return `Repair stale ${kind} reference`;
  return `Link ${kind} to scoped catalog evidence`;
}

function buildCatalogLinkRows(
  ontology: WorkspaceOntology,
  allowlist: StructuredAssetAllowlistRow[],
  entityById: Map<string, OntologyEntity>,
): CatalogLinkAuditRow[] {
  const rows: CatalogLinkAuditRow[] = [];

  ontology.entities.forEach((entity) => {
    const status = getOntologyCatalogLinkStatus(entity, allowlist);
    rows.push({
      id: `entity-${entity.id}`,
      artifactLabel: entity.name || 'Unnamed entity',
      artifactType: 'Entity',
      physicalRef: catalogAssetLabel(entity, allowlist),
      status,
      actionNeeded: catalogActionForStatus(status, 'entity'),
      selection: { kind: 'entity', id: entity.id },
    });
  });

  (ontology.semanticMappings ?? []).forEach((mapping) => {
    const status = conceptCatalogStatus(mapping, allowlist);
    rows.push({
      id: `business-meaning-${mapping.id}`,
      artifactLabel: mapping.label || mapping.conceptKey,
      artifactType: 'Business meaning',
      physicalRef: `${mapping.physicalTable || 'No table'} · column ${mapping.columnId || 'missing'}`,
      status,
      actionNeeded: catalogActionForStatus(status, 'column'),
      selection: { kind: 'businessMeaning', id: mapping.id },
    });
  });

  ontology.metrics.forEach((metric) => {
    const entity = entityById.get(metric.entityId);
    const status = entity ? getOntologyCatalogLinkStatus(entity, allowlist) : 'invalid';
    rows.push({
      id: `metric-${metric.id}`,
      artifactLabel: metric.name || 'Unnamed metric',
      artifactType: 'Metric',
      physicalRef: metric.primaryColumnId ? `Primary column ${metric.primaryColumnId}` : 'No primary column reference',
      status: metric.primaryColumnId ? status : 'partial',
      actionNeeded: metric.primaryColumnId ? catalogActionForStatus(status, 'metric input') : 'Link metric to a catalog-backed business meaning or column',
      selection: { kind: 'metric', id: metric.id },
    });
  });

  ontology.joins.forEach((join) => {
    const leftEntity = entityById.get(join.leftEntityId);
    const rightEntity = entityById.get(join.rightEntityId);
    const leftStatus = leftEntity ? getOntologyCatalogLinkStatus(leftEntity, allowlist) : 'invalid';
    const rightStatus = rightEntity ? getOntologyCatalogLinkStatus(rightEntity, allowlist) : 'invalid';
    const status: OntologyCatalogLinkStatus =
      relationshipTraversalAllowed(join) && leftStatus === 'linked' && rightStatus === 'linked' ? 'linked' : 'partial';
    rows.push({
      id: `relationship-${join.id}`,
      artifactLabel: join.predicate || join.name || join.description || 'Relationship',
      artifactType: 'Relationship',
      physicalRef: relationshipEvidenceLabel(join),
      status,
      actionNeeded:
        relationshipKind(join) === 'semantic_only'
          ? 'Visible in ontology, not SQL-traversable'
          : relationshipTraversalAllowed(join)
            ? catalogActionForStatus(status, 'join evidence')
            : 'Add or validate join evidence',
      selection: { kind: 'join', id: join.id },
    });
  });

  return rows;
}

function metricSemanticInputCount(metric: OntologyMetric, semanticMappings: OntologySemanticMapping[]): number {
  return semanticMappings.filter(
    (mapping) =>
      mapping.entityId === metric.entityId ||
      (metric.primaryColumnId && mapping.columnId === metric.primaryColumnId) ||
      JSON.stringify(metric.expressionMetadata ?? {}).includes(mapping.conceptKey),
  ).length;
}

function slugifyIdentifier(value: string): string {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug || 'entity';
}

function ValidationStatusBadge({
  issues,
  label,
  variant,
}: {
  issues: ValidationIssue[];
  label: string;
  variant: BadgeProps['variant'];
}) {
  const [isOpen, setIsOpen] = useState(false);

  if (issues.length === 0) {
    return <Badge variant={variant}>{label}</Badge>;
  }

  return (
    <div
      className="relative inline-flex"
      onPointerEnter={() => setIsOpen(true)}
      onPointerLeave={() => setIsOpen(false)}
      onMouseEnter={() => setIsOpen(true)}
      onMouseOver={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onFocusCapture={() => setIsOpen(true)}
      onBlurCapture={() => setIsOpen(false)}
    >
      <Badge
        variant={variant}
        tabIndex={0}
        className="cursor-help"
        aria-describedby="ontology-validation-issues"
        title={issues.map((issue) => `${issue.label}: ${issue.detail}`).join('\n')}
      >
        {label}
      </Badge>
      <div
        id="ontology-validation-issues"
        role="tooltip"
        className={cn(
          'pointer-events-none absolute left-0 top-full z-50 mt-2 w-80 rounded-lg border border-gray-200 bg-white p-3 text-left shadow-lg dark:border-dark-border dark:bg-dark-surface',
          isOpen ? 'block' : 'hidden',
        )}
      >
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
          Draft validation
        </p>
        <div className="mt-2 space-y-2">
          {issues.map((issue) => (
            <div key={issue.id} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300">
              <AlertTriangle
                className={cn(
                  'mt-0.5 h-4 w-4 shrink-0',
                  issue.severity === 'error' ? 'text-rose-500' : 'text-amber-500',
                )}
              />
              <span>
                <span className="block font-semibold text-charcoal dark:text-gray-100">{issue.label}</span>
                <span className="mt-0.5 block leading-5">{issue.detail}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmptyInspectorState({
  readOnly,
  onCreateEntity,
  onCreateBusinessMeaning,
  onCreateMetric,
  onCreateJoin,
  onCollapse,
}: {
  readOnly: boolean;
  onCreateEntity: () => void;
  onCreateBusinessMeaning: () => void;
  onCreateMetric: () => void;
  onCreateJoin: () => void;
  onCollapse: () => void;
}) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-5">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2/60">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-eliza-red/10 text-eliza-red">
            <Bot className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
              Builder assistant
            </p>
            <h3 className="mt-1 text-sm font-semibold text-charcoal dark:text-gray-100">Start the ontology draft</h3>
            <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
              Select a graph item to inspect it, or start from scoped assets with manual controls. AI creation is reserved for the governed suggestion workflow.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Collapse inspector"
            title="Collapse inspector"
            onClick={onCollapse}
          >
            <ChevronRight className="h-4 w-4 text-gray-500" />
          </Button>
        </div>

        {!readOnly && (
          <div className="mt-4 grid gap-2">
            <Button variant="secondary" size="sm" onClick={onCreateEntity}>
              <Plus className="h-4 w-4" />
              Add entity
            </Button>
            <Button variant="outline" size="sm" onClick={onCreateBusinessMeaning}>
              <Plus className="h-4 w-4" />
              Business meaning
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" onClick={onCreateMetric}>
                <Plus className="h-4 w-4" />
                Metric
              </Button>
              <Button variant="outline" size="sm" onClick={onCreateJoin}>
                <Plus className="h-4 w-4" />
                Join
              </Button>
            </div>
          </div>
        )}

        <div className="mt-4 space-y-2 border-t border-gray-200 pt-4 dark:border-dark-border">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
            AI assistance
          </p>
          <PlaceholderAssistantButton icon={WandSparkles} label="Suggest entities" />
          <PlaceholderAssistantButton icon={Link2} label="Infer joins" />
          <PlaceholderAssistantButton icon={Sparkles} label="Generate metrics" />
          <PlaceholderAssistantButton icon={Sparkles} label="Enrich aliases" />
        </div>
      </div>
    </div>
  );
}

function CollapsedInspectorRail({
  issueCount,
  hasSelection,
  onExpand,
}: {
  issueCount: number;
  hasSelection: boolean;
  onExpand: () => void;
}) {
  return (
    <div
      className="flex h-full w-14 flex-col items-center border-l border-gray-200 bg-white py-3 dark:border-dark-border dark:bg-dark-surface"
      data-testid="collapsed-inspector-rail"
    >
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Expand inspector"
        title="Expand inspector"
        aria-expanded={false}
        onClick={onExpand}
      >
        <ChevronLeft className="h-4 w-4 text-gray-500" />
      </Button>

      <div
        className="mt-6 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500"
        style={{ writingMode: 'vertical-rl' }}
      >
        <span>{hasSelection ? 'Inspector' : 'Assistant'}</span>
      </div>

      {issueCount > 0 && (
        <div
          className="mt-auto flex h-8 w-8 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-xs font-semibold text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300"
          title={`${issueCount} validation issue${issueCount === 1 ? '' : 's'}`}
        >
          {issueCount}
        </div>
      )}
    </div>
  );
}

function PlaceholderAssistantButton({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <button
      type="button"
      disabled
      title="Coming soon"
      className="flex w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-xs text-gray-400 dark:border-dark-border dark:bg-dark-surface"
    >
      <span className="inline-flex items-center gap-2">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </span>
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-dark-surface-2 dark:text-gray-400">
        Coming soon
      </span>
    </button>
  );
}

function normalizeOntologyDraft(ontology: WorkspaceOntology): WorkspaceOntology {
  return {
    ...ontology,
    semanticMappings: ontology.semanticMappings ?? [],
    entities: ontology.entities.map((entity) => ({
      ...entity,
      synonyms: entity.synonyms ?? [],
      sourceBindings: entity.sourceBindings ?? [],
    })),
    joins: ontology.joins.map((join) => ({
      ...join,
      relationshipKind: join.relationshipKind ?? ((join.joinCondition ?? '').trim() ? 'join_backed' : 'semantic_only'),
      predicate: join.predicate ?? join.name ?? null,
      optionality: join.optionality ?? 'unknown',
      joinCondition: join.joinCondition ?? '',
    })),
  };
}

function mergeIncomingOntology(current: WorkspaceOntology, incoming: WorkspaceOntology): WorkspaceOntology {
  const normalizedIncoming = normalizeOntologyDraft(incoming);
  const currentDraft = normalizeOntologyDraft(current);
  return {
    entities: mergeByIdentity(
      currentDraft.entities,
      normalizedIncoming.entities,
      (entity) => [entity.id, entity.physicalTable, entity.name],
    ),
    semanticMappings: mergeByIdentity(
      currentDraft.semanticMappings ?? [],
      normalizedIncoming.semanticMappings ?? [],
      (mapping) => [mapping.id, mapping.conceptKey, mapping.columnId],
    ),
    metrics: mergeByIdentity(
      currentDraft.metrics,
      normalizedIncoming.metrics,
      (metric) => [metric.id, metric.primaryColumnId ?? '', metric.name],
    ),
    joins: mergeByIdentity(
      currentDraft.joins,
      normalizedIncoming.joins,
      (join) => [join.id, join.predicate ?? '', join.joinCondition, `${join.leftEntityId}:${join.rightEntityId}`],
    ),
  };
}

function mergeByIdentity<T>(current: T[], incoming: T[], keysFor: (item: T) => string[]): T[] {
  const merged = [...current];
  const knownKeys = new Set(current.flatMap((item) => keysFor(item).filter(Boolean)));
  incoming.forEach((item) => {
    const keys = keysFor(item).filter(Boolean);
    if (keys.some((key) => knownKeys.has(key))) {
      return;
    }
    merged.push(item);
    keys.forEach((key) => knownKeys.add(key));
  });
  return merged;
}

function selectFirstAvailable(ontology: WorkspaceOntology): Selection {
  if (ontology.entities[0]) return { kind: 'entity', id: ontology.entities[0].id };
  if (ontology.metrics[0]) return { kind: 'metric', id: ontology.metrics[0].id };
  if (ontology.joins[0]) return { kind: 'join', id: ontology.joins[0].id };
  return null;
}

function validateOntology(ontology: WorkspaceOntology, allowlist: StructuredAssetAllowlistRow[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const entityIds = new Set(ontology.entities.map((entity) => entity.id));
  const allowedTableRefs = new Set([
    ...allowlist.map((row) => `table:${row.id}`),
    ...allowlist.map((row) => `${row.dataset}.${row.objectName}`),
  ]);

  if (ontology.entities.length === 0) {
    issues.push({
      id: 'empty-entities',
      severity: 'error',
      label: 'No entities',
      detail: 'Add at least one business entity mapped to a scoped warehouse asset.',
    });
  }

  ontology.entities.forEach((entity) => {
    if (!entity.description.trim()) {
      issues.push({
        id: `entity-description-${entity.id}`,
        severity: 'warning',
        label: `${entity.name} needs a description`,
        detail: 'Descriptions help agents choose the right entity and explain generated SQL.',
      });
    }
    if (!entity.physicalTable || (allowlist.length > 0 && !allowedTableRefs.has(entity.physicalTable))) {
      issues.push({
        id: `entity-asset-${entity.id}`,
        severity: 'error',
        label: `${entity.name} is not mapped`,
        detail: 'Map the entity to a scoped table or view before pack activation.',
      });
    }
    const bindings = entity.sourceBindings ?? [];
    if (bindings.length > 0 && !bindings.some((binding) => binding.reviewState === 'approved')) {
      issues.push({
        id: `entity-bindings-${entity.id}`,
        severity: 'error',
        label: `${entity.name} has no approved source binding`,
        detail: 'Review at least one physical source binding before pack activation.',
      });
    }
    bindings.forEach((binding) => {
      if (
        allowlist.length > 0
        && !allowedTableRefs.has(`table:${binding.tableId}`)
      ) {
        issues.push({
          id: `entity-binding-scope-${entity.id}-${binding.tableId}`,
          severity: 'error',
          label: `${entity.name} references an out-of-scope source`,
          detail: `Table ${binding.tableId} is not in the approved workspace allowlist.`,
        });
      }
      if (binding.reviewState !== 'approved') {
        issues.push({
          id: `entity-binding-review-${entity.id}-${binding.tableId}`,
          severity: 'warning',
          label: `${entity.name} has a ${binding.reviewState} source binding`,
          detail: 'Only approved source bindings are compiled into the Runtime Context Pack.',
        });
      }
    });
  });

  ontology.metrics.forEach((metric) => {
    if (!entityIds.has(metric.entityId)) {
      issues.push({
        id: `metric-entity-${metric.id}`,
        severity: 'error',
        label: `${metric.name} has no parent entity`,
        detail: 'Metrics must attach to a governed entity.',
      });
    }
    if (!metric.expression.trim()) {
      issues.push({
        id: `metric-expression-${metric.id}`,
        severity: 'error',
        label: `${metric.name} needs SQL`,
        detail: 'Add a SQL expression so the compiler can validate the metric.',
      });
    }
  });

  ontology.joins.forEach((join) => {
    if (!entityIds.has(join.leftEntityId) || !entityIds.has(join.rightEntityId)) {
      issues.push({
        id: `join-entity-${join.id}`,
        severity: 'error',
        label: 'Relationship references a missing entity',
        detail: 'Both sides of a relationship must resolve to current ontology entities.',
      });
    }
    if (!(join.predicate || join.name || '').trim()) {
      issues.push({
        id: `join-predicate-${join.id}`,
        severity: 'warning',
        label: 'Relationship needs a business predicate',
        detail: 'Use a predicate such as books, contains, summarizes, or belongs to so ontology edges do not read like SQL joins.',
      });
    }
    if (relationshipKind(join) !== 'semantic_only' && !relationshipTraversalAllowed(join)) {
      issues.push({
        id: `join-evidence-${join.id}`,
        severity: 'error',
        label: 'Join-backed relationship needs validated evidence',
        detail: 'Semantic-only relationships can compile for ontology context, but join-backed relationships need join evidence before SQL traversal.',
      });
    }
  });

  return issues;
}
